import type { CommunityContext, CommunityRole } from '@/types/community'

import {
    COMMUNITY_ANALYTICS_ROLES,
    COMMUNITY_LISTING_ROLES,
    COMMUNITY_MODERATION_ROLES,
    COMMUNITY_SETTINGS_ROLES,
    hasCommunityRole
} from '@/lib/community/authorization'

/**
 * The Community Console's capability map.
 *
 * This is the community-level counterpart of `@/lib/auth/permissions`, and it
 * exists for the same reason: both sides of the console need the identical
 * answer.
 *
 *   - the server guards (`@/lib/community/console-guard`) use it to ADMIT or
 *     REFUSE a request for one section;
 *   - the console navigation uses it to SHOW or HIDE the entry that leads
 *     there.
 *
 * It restates NO role rule of its own. Every capability defers to the arrays
 * already declared in `@/lib/community/authorization`, so widening a section's
 * audience is one edit there and nowhere else. There are no server-only imports
 * here, so the navigation can ask the question without pulling the database
 * client into the browser bundle.
 *
 * The membership itself always comes from a `CommunityContext` that
 * `@/lib/community/context` re-read from the database on THIS request. A role
 * carried in a body, query string or cookie never reaches any of this.
 */

export type ConsoleCapability = 'settings' | 'moderation' | 'listings' | 'analytics'

const CAPABILITY_ROLES: Record<ConsoleCapability, Array<CommunityRole>> = {
    settings: COMMUNITY_SETTINGS_ROLES,
    moderation: COMMUNITY_MODERATION_ROLES,
    listings: COMMUNITY_LISTING_ROLES,
    analytics: COMMUNITY_ANALYTICS_ROLES
}

/** True when this context may act on `capability` inside its own community. */
export const hasConsoleCapability = (context: CommunityContext | null, capability: ConsoleCapability): boolean =>
    hasCommunityRole(context, CAPABILITY_ROLES[capability])

export type ConsoleSectionKey =
    | 'overview'
    | 'listings'
    | 'sellers'
    | 'moderation'
    | 'categories'
    | 'rules'
    | 'telegram'
    | 'team'
    | 'settings'

export type ConsoleSection = {
    key: ConsoleSectionKey
    label: string
    /** The single capability both the nav entry and the route behind it ask. */
    capability: ConsoleCapability
    /** Grouping heading, or null for the leading group. */
    heading: string | null
}

/**
 * Every section of the Community Console, in sidebar order.
 *
 * This list is deliberately free of PLATFORM entries. A community operator
 * manages one marketplace: there is no directory of communities here, no
 * platform users, no platform settings, no platform revenue and no platform
 * audit administration. Those live at `/admin/communities` behind
 * `canAdministerPlatform`, and nothing in this file can reach them.
 */
export const CONSOLE_SECTIONS: Array<ConsoleSection> = [
    { key: 'overview', label: 'Overview', capability: 'analytics', heading: null },
    { key: 'listings', label: 'Listings', capability: 'listings', heading: 'Marketplace' },
    { key: 'sellers', label: 'Sellers', capability: 'moderation', heading: 'Marketplace' },
    { key: 'moderation', label: 'Moderation', capability: 'moderation', heading: 'Marketplace' },
    { key: 'categories', label: 'Categories', capability: 'settings', heading: 'Setup' },
    { key: 'rules', label: 'Rules', capability: 'settings', heading: 'Setup' },
    { key: 'telegram', label: 'Telegram', capability: 'settings', heading: 'Setup' },
    { key: 'team', label: 'Team', capability: 'settings', heading: 'Setup' },
    { key: 'settings', label: 'Settings', capability: 'settings', heading: 'Setup' }
]

const SECTION_BY_KEY = new Map(CONSOLE_SECTIONS.map((section) => [section.key, section]))

/** The capability a section requires. Unknown keys are refused, never admitted. */
export const capabilityForSection = (key: ConsoleSectionKey): ConsoleCapability | null =>
    SECTION_BY_KEY.get(key)?.capability ?? null

/**
 * The sections one membership may see, in order.
 *
 * The sidebar renders exactly this and nothing else decides visibility, so the
 * rule can be exercised without mounting the console. Hiding a section is UX
 * only: `requireConsolePage` and `requireConsoleApi` re-check the same
 * capability on every request, and `console-gate.test.ts` asserts the two never
 * disagree.
 */
export const visibleConsoleSections = (context: CommunityContext | null): Array<ConsoleSection> =>
    CONSOLE_SECTIONS.filter((section) => hasConsoleCapability(context, section.capability))

/**
 * Whether this identity may open the console at all.
 *
 * Derived, never a second role list: holding any one capability is what opens
 * the door, so an `analyst` gets in and sees Overview alone, while a plain
 * `member` holds nothing and is refused. Adding a capability above
 * automatically widens this in step.
 */
export const canOpenConsole = (context: CommunityContext | null): boolean =>
    visibleConsoleSections(context).length > 0

/**
 * The same question asked of a SEAT alone, with no community attached.
 *
 * Used by the switcher, which lists the marketplaces one identity may open and
 * therefore needs the answer for several communities at once. It reads the same
 * capability map, so a role that opens nothing never appears as a dead entry.
 * The seat's status is not consulted here because the query that produces these
 * rows already returns active seats only.
 */
export const roleOpensConsole = (role: CommunityRole): boolean =>
    CONSOLE_SECTIONS.some((section) => CAPABILITY_ROLES[section.capability].includes(role))

/** Route for one section of one community. The id is always a resolved one. */
export const consolePath = (communityId: string, key: ConsoleSectionKey = 'overview'): string =>
    key === 'overview' ? `/admin/community/${communityId}` : `/admin/community/${communityId}/${key}`
